import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { PERMISSION_GROUPS_URL, useGroupedPermissions } from '../../../../api/permissions';
import { PermissionGroup, GroupedPermission } from '../../../../types/permissions';
import styles from './tenantPermissionGroupDetail.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';

export const TenantPermissionGroupDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading, get } = useCRUD(PERMISSION_GROUPS_URL);
  const { setOverride } = useBreadcrumbs();
  const { query: queryGroupedPermissions, loading: permissionsLoading } = useGroupedPermissions();
  const [group, setGroup] = useState<PermissionGroup | undefined>();
  const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermission[]>([]);
  const [matchedGroups, setMatchedGroups] = useState<GroupedPermission[]>([]);
  const canViewGroups = useHasPermission("auth.view_group");

  const fetchGroup = useCallback(async () => {
    if (!id) return;

    const result = await get(id);

    if (result?.success && result?.data) {
      setGroup(result.data);
      setOverride(`/tenant/permission-groups/${id}`, result.data.name || '');
    } else {
      navigate('/tenant/permission-groups');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get, navigate]);

  useEffect(() => {
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('permissionGroupsUpdated', fetchGroup);

    return () => {
      window.removeEventListener('permissionGroupsUpdated', fetchGroup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadGroupedPermissions = async () => {
      const data = await queryGroupedPermissions();
      if (data) {
        setGroupedPermissions(data);
      }
    };
    loadGroupedPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!group || !groupedPermissions.length) {
      setMatchedGroups([]);
      return;
    }

    // Create a set of permission IDs from the group
    const groupPermissionIds = new Set(
      group.permissions?.map((p) => p.id.toString()) || []
    );

    // Find grouped permissions where at least one subgroup has all permissions included in the group
    const matched = groupedPermissions.filter((groupedPerm) => {
      return groupedPerm.subgroups.some((subgroup) => {
        const subgroupPermissionIds = Object.keys(subgroup.permissions);
        return (
          subgroupPermissionIds.length > 0 &&
          subgroupPermissionIds.every((id) => groupPermissionIds.has(id))
        );
      });
    });

    setMatchedGroups(matched);
  }, [group, groupedPermissions]);

  if (!canViewGroups) {
    return null;
  }

  if (loading || permissionsLoading || !group) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Typography variant="subtitle1">
          <strong>{t('Name')}:</strong> {group.name || ''}
        </Typography>
        <Typography variant="subtitle1">
          <strong>{t('Members can be assigned to jobs as technicians')}:</strong>{' '}
          {group.is_service_role ? t('Yes') : t('No')}
        </Typography>
      </Box>
      <Divider />
      <Box className={styles.permissionsSection}>
        <Typography variant="subtitle1">
          <strong>{t('Permissions')}:</strong>
        </Typography>
        <Box className={styles.permissionsList}>
          {matchedGroups.length > 0 ? (
            matchedGroups.map((groupedPerm) => {
              const groupPermissionIds = new Set(
                group?.permissions?.map((p) => p.id.toString()) || []
              );
              const matchedSubgroups = groupedPerm.subgroups.filter((subgroup) => {
                const subgroupPermissionIds = Object.keys(subgroup.permissions);
                return (
                  subgroupPermissionIds.length > 0 &&
                  subgroupPermissionIds.every((id) => groupPermissionIds.has(id))
                );
              });

              return (
                <Box key={groupedPerm.id} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {groupedPerm.name}
                  </Typography>
                  {matchedSubgroups.length > 0 && (
                    <Box sx={{ ml: 2 }}>
                      {matchedSubgroups.map((subgroup, index) => (
                        <Chip
                          key={index}
                          label={subgroup.name}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('No permissions assigned')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
